
import { FcGoogle } from 'react-icons/fc';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrengthMeter } from "@/components/ui/password-strength-meter";
import { PasswordRules } from "@/components/ui/password-rules";
import { useAuthForm } from "@/hooks/useAuthForm";
import { usePasswordValidation } from "@/hooks/usePasswordValidation";
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from "react-router-dom";

interface SignUpFormProps {
  returnTo?: string;
  onSignUpSuccess?: () => void;
}

export function SignUpForm({ returnTo = '/', onSignUpSuccess }: SignUpFormProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { 
    email, 
    setEmail, 
    setPassword: setAuthPassword,
    loading, 
    handleSignUp,
    handleGoogleSignIn
  } = useAuthForm();
  
  const passwordValidation = usePasswordValidation({ enableConfirmPassword: true });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAuthPassword(passwordValidation.password);
  }, [passwordValidation.password, setAuthPassword]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (passwordValidation.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (passwordValidation.password !== passwordValidation.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!passwordValidation.isValid) {
      setError('Password is too weak. Please follow the guidelines below.');
      return;
    }
    
    const result = await handleSignUp(e, returnTo);
    
    if (result && result.error) {
      setError(result.error.message || 'An error occurred during sign up');
    } else {
      onSignUpSuccess?.();
    }
  };
  
  const handleGoogleClick = async () => {
    setError(null);
    await handleGoogleSignIn("/complete-registration");
  };

  const getSignInUrl = () => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete('mode'); // Remove mode to default to sign in
    return currentParams.toString() ? `?${currentParams.toString()}` : '/auth';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">Create Account</h2>
        <p className="text-sm text-muted-foreground mt-2">Join our community and get started</p>
      </div>

      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="p-4 text-sm bg-destructive/15 text-destructive rounded-lg border border-destructive/20"
        >
          {error}
        </motion.div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-muted-foreground">
              {t('auth.signup.email')}
            </Label>
            <Input 
              id="email" 
              type="email" 
              placeholder={t('auth.signup.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 px-4 text-base border-input focus:border-primary focus:ring-primary focus:ring-2 transition-all"
              required
              aria-label="Email address"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-muted-foreground">
              {t('auth.signup.password')}
            </Label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={passwordValidation.password}
              onChange={(e) => passwordValidation.setPassword(e.target.value)}
              onFocus={() => {
                passwordValidation.setPasswordFocused(true);
                passwordValidation.setPasswordTouched(true);
              }}
              onBlur={() => {
                passwordValidation.setPasswordFocused(false);
              }}
              className="h-12 px-4 text-base border-input focus:border-primary focus:ring-primary focus:ring-2 transition-all"
              required
              aria-label="Password"
            />
            
            <PasswordStrengthMeter 
              password={passwordValidation.password} 
              strength={passwordValidation.passwordStrength} 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm text-muted-foreground">
              {t('auth.signup.confirmPassword', 'Confirm Password')}
            </Label>
            <PasswordInput
              id="confirmPassword"
              placeholder="••••••••"
              value={passwordValidation.confirmPassword}
              onChange={(e) => passwordValidation.setConfirmPassword(e.target.value)}
              className="h-12 px-4 text-base border-input focus:border-primary focus:ring-primary focus:ring-2 transition-all"
              required
              aria-label="Confirm password"
            />
            {passwordValidation.confirmPassword && passwordValidation.password !== passwordValidation.confirmPassword && (
              <p className="text-sm text-destructive mt-1">Passwords do not match</p>
            )}
          </div>
          
          <PasswordRules
            shouldShow={passwordValidation.shouldShowRules}
            rules={passwordValidation.passwordRules}
            showPasswordsMatch={passwordValidation.confirmPassword !== ''}
          />
        </div>
        
        <div className="space-y-4">
          <Button
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2"
            type="submit"
            disabled={loading || passwordValidation.passwordStrength === 'weak' || passwordValidation.passwordStrength === null || !passwordValidation.passwordRules.passwordsMatch}
            isLoading={loading}
          >
            {t('auth.signup')}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border"></span>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-card px-4 text-muted-foreground">or</span>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            type="button" 
            onClick={handleGoogleClick}
            className="w-full h-12 font-medium border-border hover:bg-accent hover:text-accent-foreground transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2"
            disabled={loading}
          >
            <FcGoogle className="mr-3 h-5 w-5" />
            Continue with Google
          </Button>
        </div>
      </form>
      
      <div className="text-center">
        <span className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link 
            to={getSignInUrl()}
            className="text-primary hover:text-primary/90 underline-offset-4 hover:underline font-medium transition-colors"
          >
            Sign in
          </Link>
        </span>
      </div>
    </motion.div>
  );
}
